import { supabase } from './supabaseClient';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TIMEOUT_MS = 30000;

let _impersonatingId = null;
export function setImpersonatingId(id) { _impersonatingId = id; }
export function getImpersonatingId() { return _impersonatingId; }

// Cached Supabase access token so callers can read it synchronously.
let _accessToken = null;

// Initialise the cache and keep it in sync with the Supabase session
// (login, logout, automatic token refresh, OAuth redirect).
supabase.auth.getSession().then(({ data }) => {
  _accessToken = data.session?.access_token || null;
});
supabase.auth.onAuthStateChange((_event, session) => {
  _accessToken = session?.access_token || null;
});

// Returns the current Supabase access token (or null). Synchronous.
export function getToken() {
  return _accessToken;
}

// Kept for backwards compatibility — Supabase now owns the session, so writing
// a token manually is a no-op. The cache updates via onAuthStateChange above.
export function setToken() {}

// Sign the user out of Supabase and clear the cached token.
export function clearToken() {
  _accessToken = null;
  supabase.auth.signOut();
}

export async function apiFetch(path, options = {}) {
  // `timeoutMs` is an apiFetch-only option (not a fetch option) — long-running
  // calls (onboarding crawl/build, profile create) pass a larger value so a slow
  // LLM step doesn't get aborted at the 30s default. Strip it before fetch().
  const { timeoutMs = TIMEOUT_MS, ...fetchOptions } = options;

  // Prefer the cached token; fall back to a fresh session lookup if the cache
  // hasn't populated yet (e.g. immediately after a hard page reload).
  let token = _accessToken;
  if (!token) {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token || null;
    _accessToken = token;
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(_impersonatingId ? { 'X-Impersonate-User': _impersonatingId } : {}),
    ...(fetchOptions.headers || {}),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Request timed out. Please try again.');
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    _accessToken = null;
    await supabase.auth.signOut();
    window.location.href = '/login';
    return;
  }

  if (!res.ok) {
    let errorMsg = `Request failed: ${res.status}`;
    try {
      const err = await res.json();
      errorMsg = err.detail || errorMsg;
    } catch (_) {}
    throw new Error(errorMsg);
  }

  if (res.status === 204) return null;

  return res.json();
}
